import React, { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { tex } from './textures';
import { wingWindows } from './model3d';

export const ORANGE = '#F26B1F';
const T = 0.3;
const dummy = new THREE.Object3D();

export const Mat = ({ color = '#ffffff', map, hl, clip, opacity = 1, emissive, emissiveIntensity = 0, metalness = 0.02, roughness = 0.88, envMapIntensity = 1 }) => (
  <meshStandardMaterial
    color={hl ? ORANGE : color} map={hl ? null : map}
    emissive={hl ? ORANGE : emissive || '#000000'} emissiveIntensity={hl ? 0.45 : emissiveIntensity}
    clippingPlanes={clip} clipShadows transparent={opacity < 1} opacity={opacity}
    metalness={metalness} roughness={roughness} envMapIntensity={envMapIntensity}
  />
);

export const Box = ({ args, position, children, shadow = true, ...rest }) => (
  <mesh position={position} castShadow={shadow} receiveShadow {...rest}>
    <boxGeometry args={args} />
    {children}
  </mesh>
);

const Instanced = ({ items, scale, children }) => {
  const ref = useRef();
  useLayoutEffect(() => {
    const m = ref.current;
    if (!m) return;
    items.forEach((it, i) => {
      dummy.position.set(...it.p);
      dummy.rotation.set(0, it.rot ? Math.PI / 2 : 0, 0);
      dummy.scale.set(...scale);
      dummy.updateMatrix();
      m.setMatrixAt(i, dummy.matrix);
    });
    m.instanceMatrix.needsUpdate = true;
    m.computeBoundingSphere();
  }, [items, scale]);
  if (!items.length) return null;
  return (
    <instancedMesh ref={ref} args={[null, null, items.length]} key={items.length} receiveShadow>
      <boxGeometry />
      {children}
    </instancedMesh>
  );
};

const lit = (x, y, f) => (Math.abs(Math.round(x * 7 + y * 13 + f * 5)) % 10) < 3;

const useWindows = (wing, wings, H, fv, spacing, entrance) => useMemo(() => {
  const wh = Math.min(1.7, H - 1.3);
  const out = { glass: [], glassLit: [], frame: [], mull: [], sill: [], wh };
  for (let f = 0; f < fv; f += 1) {
    wingWindows(wing, wings, f, spacing).forEach((w) => {
      if (entrance && f === 0 && w.side === 0 && Math.abs(w.x - entrance) < 3) return;
      const cy = f * H + 0.9 + wh / 2;
      const at = (o, y = cy) => [w.x + w.nx * o, y, w.y + w.ny * o];
      (lit(w.x, w.y, f) ? out.glassLit : out.glass).push({ p: at(T / 2 + 0.02), rot: w.rot });
      out.frame.push({ p: at(T / 2 + 0.005), rot: w.rot });
      out.mull.push({ p: at(T / 2 + 0.05), rot: w.rot });
      out.sill.push({ p: at(T / 2 + 0.07, f * H + 0.88), rot: w.rot });
    });
  }
  return out;
}, [wing, wings, H, fv, spacing, entrance]);

const WingWindows = ({ win, hl, clip }) => {
  const s = win.wh;
  return (
    <group>
      <Instanced items={win.glass} scale={[1.45, s, 0.04]}><Mat color="#1d2a33" hl={hl} clip={clip} metalness={0.9} roughness={0.08} envMapIntensity={1.6} /></Instanced>
      <Instanced items={win.glassLit} scale={[1.45, s, 0.04]}><Mat color="#3a2c1c" hl={hl} clip={clip} emissive="#ffb066" emissiveIntensity={0.9} metalness={0.4} roughness={0.2} /></Instanced>
      <Instanced items={win.frame} scale={[1.62, s + 0.16, 0.05]}><Mat color="#2b2b2c" hl={hl} clip={clip} metalness={0.5} roughness={0.4} /></Instanced>
      <Instanced items={win.mull} scale={[0.06, s, 0.05]}><Mat color="#2b2b2c" hl={hl} clip={clip} metalness={0.5} roughness={0.4} /></Instanced>
      <Instanced items={win.sill} scale={[1.75, 0.06, 0.16]}><Mat color="#a9a59f" hl={hl} clip={clip} /></Instanced>
    </group>
  );
};

const Entrance = ({ x, y, hl, clip }) => (
  <group>
    <Box args={[4.4, 2.9, 0.06]} position={[x, 1.45, y - T / 2 - 0.03]} shadow={false}>
      <Mat color="#1d2a33" hl={hl} clip={clip} emissive="#ffb066" emissiveIntensity={0.35} metalness={0.8} roughness={0.08} />
    </Box>
    <Box args={[5.6, 0.22, 2.2]} position={[x, 3.1, y - 1.1]}><Mat color="#2b2b2c" clip={clip} metalness={0.4} roughness={0.5} /></Box>
    <Box args={[0.12, 3.0, 0.12]} position={[x - 2.6, 1.5, y - 2.05]}><Mat color="#2b2b2c" clip={clip} /></Box>
    <Box args={[0.12, 3.0, 0.12]} position={[x + 2.6, 1.5, y - 2.05]}><Mat color="#2b2b2c" clip={clip} /></Box>
  </group>
);

const ring = (wing, h, y, t) => [
  { args: [wing.w + t, h, t], p: [wing.x + wing.w / 2, y, wing.y] },
  { args: [wing.w + t, h, t], p: [wing.x + wing.w / 2, y, wing.y + wing.d] },
  { args: [t, h, wing.d], p: [wing.x, y, wing.y + wing.d / 2] },
  { args: [t, h, wing.d], p: [wing.x + wing.w, y, wing.y + wing.d / 2] },
];

export const WingShell = ({ wing, wings, H, fv, facade, spacing, show, hl, clip, entrance }) => {
  const win = useWindows(wing, wings, H, fv, spacing, entrance);
  if (fv <= 0) return null;
  const height = fv * H;
  return (
    <group>
      {show.extWalls && ring(wing, height, height / 2, T).map((s, i) => (
        <Box key={i} args={s.args} position={s.p}><Mat map={tex(facade, Math.max(s.args[0], s.args[2]), height)} hl={hl === 'extWalls'} clip={clip} /></Box>
      ))}
      {show.extWalls && ring(wing, 0.55, 0.275, T + 0.06).map((s, i) => (
        <Box key={`p${i}`} args={s.args} position={s.p}><Mat color="#4b4845" hl={hl === 'extWalls'} clip={clip} /></Box>
      ))}
      {show.slabs && Array.from({ length: fv - 1 }, (_, k) => ring(wing, 0.24, (k + 1) * H, T + 0.08).map((s, i) => (
        <Box key={`b${k}-${i}`} args={s.args} position={s.p}><Mat color="#bdb8b0" hl={hl === 'slabs'} clip={clip} /></Box>
      )))}
      {show.windows && <WingWindows win={win} hl={hl === 'windows'} clip={clip} />}
      {show.windows && entrance != null && <Entrance x={entrance} y={wing.y} hl={hl === 'windows'} clip={clip} />}
    </group>
  );
};
